import React from 'react';
import { DndContext, closestCenter, DragEndEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    arrayMove,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { Box } from "@mui/material";
import SortableWipeCard from "./SortableWipeCard";
import {CollectionClip} from "../../../types/collectionTypes";
import {FilterOption} from "../../../types/filterTypes";
import {DimensionCardsMulti} from "../../../contexts/application/DimensionCardsProvider";


interface SimpleComposerProps {
    keyMoments: CollectionClip[];
    onChangeOrder: (ordered: CollectionClip[]) => void;
    onCardRemove: (id: string) => void;
    onCardLockChange: (id: string) => void;
    gridView: boolean
    eventsAudioTracks?: Record<string, string[]>
    audioTrack?: FilterOption | null
}

export default function SimpleComposer({ keyMoments, onChangeOrder, onCardRemove, onCardLockChange, gridView, eventsAudioTracks = {}, audioTrack = null }: SimpleComposerProps) {
    const ids = keyMoments.map((km) => km.id);
    const [activeId, setActiveId] = React.useState<string | null>(null);
    // Le card sono sempre renderizzate "smaller" (vedi ClipCard/MemoClipCard), quindi la
    // griglia deve dimensionarsi sulla misura small a prescindere dall'opzione Small/Medium/Large.
    const dimensionCard = DimensionCardsMulti.SMALL

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;
        if (active.id === over.id) return;

        const oldIndex = ids.indexOf(String(active.id));
        const newIndex = ids.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;

        const reordered = arrayMove(keyMoments, oldIndex, newIndex);
        onChangeOrder(reordered);
        setActiveId(null); 
    };

    return (
        <DndContext 
            onDragStart={(event: DragStartEvent) => {
                setActiveId(event.active.id as string); // imposta la card attiva
            }}
            onDragCancel={() => setActiveId(null)}
            collisionDetection={closestCenter} 
            onDragEnd={handleDragEnd}>
            <SortableContext
                items={ids}
                strategy={gridView ? rectSortingStrategy : horizontalListSortingStrategy}
            >
                {keyMoments && keyMoments.length > 0 && <Box
                    sx={{
                        mt:2,
                        display: gridView ? "grid" : "flex",
                        ...(gridView
                        ? {
                            gridTemplateColumns: `repeat(auto-fit, minmax(${260*dimensionCard}px, 1fr))`, // usa auto-fit
                            justifyContent: "start",
                            spacing:2,
                            columnGap:2.5,
                            overflowY: "auto",
                            overflowX: "hidden",
                            paddingLeft: "3px",
                            marginLeft: "-3px",
                            paddingRight: "3px",
                            marginRight: "-3px",
                            paddingTop: "10px",
                            }
                        : {
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            overflowY: "hidden",
                            alignItems: "center",
                            gap: 2,
                            paddingLeft: "3px",
                            marginLeft: "-3px",
                            paddingRight: "3px",
                            marginRight: "-3px",
                            paddingTop: "10px",
                            //marginTop: "-10px"
                        }),
                    }}
                >
                    {keyMoments.map((km) => (
                        <SortableWipeCard
                            key={km.id}
                            id={km.id}
                            card={km}
                            onRemoveClick={() => onCardRemove(km.id)}
                            onLockClick={() => onCardLockChange(km.id)}
                            hasWarning={Boolean(
                                audioTrack &&
                                eventsAudioTracks?.[km.event.id] &&
                                !eventsAudioTracks[km.event.id].includes(audioTrack.id)
                            )}
                        />
                    ))}
                </Box>}
                
                <DragOverlay>
                    {activeId ? (
                    <SortableWipeCard
                        id={activeId}
                        card={keyMoments.find(km => km.id === activeId)!}
                    />
                    ) : null}
                </DragOverlay>
            </SortableContext>
        </DndContext>
    );
}